/**
 * Vacaciones, teletrabajo y días libres del equipo no médico (administración,
 * 14/10/2026): llamadas al servidor y reglas pequeñas de la pestaña
 * «Vacaciones y teletrabajo» de Registro de jornada
 * (components/jornada/AusenciasTab.jsx) y de la leyenda del día en la Agenda
 * (components/citas/LeyendaEquipo.jsx).
 *
 * La regla de qué tiene cada persona cada día vive en el SERVIDOR
 * (backend/services/ausencias_equipo.py): aquí no se recalcula nada, solo se
 * pinta lo que llega. También lo que llega del Cuadrante (revisión del
 * 14/10/2026): sus turnos «Vacaciones» y «Libre» vienen con
 * `origen: 'cuadrante'` y se dicen «Vacaciones (Cuadrante)».
 */
import { api } from './api';

const RAIZ = '/api/ausencias';

export async function cargarCalendario(anio, userId = null) {
  const params = { anio };
  if (userId) params.user_id = userId;
  const r = await api.get(`${RAIZ}/calendario`, { params });
  return r.data;
}

/** {fecha: [{nombre, tipo, origen?}]} entre `desde` y `hasta` (sin `hasta`, un día).
 *  Lo que no sea eso (un fallo, una respuesta vacía) se queda en {}. */
export async function leyendaEquipo(desde, hasta = null) {
  const params = { desde };
  if (hasta && hasta !== desde) params.hasta = hasta;
  const r = await api.get(`${RAIZ}/leyenda`, { params });
  const dias = r?.data?.dias;
  return dias && typeof dias === 'object' && !Array.isArray(dias) ? dias : {};
}

export async function crearPatron(datos) {
  const r = await api.post(`${RAIZ}/patrones`, datos);
  return r.data;
}

export async function editarPatron(id, datos) {
  const r = await api.put(`${RAIZ}/patrones/${id}`, datos);
  return r.data;
}

export async function desactivarPatron(id) {
  const r = await api.delete(`${RAIZ}/patrones/${id}`);
  return r.data;
}

/** tipo: 'vacaciones' | 'teletrabajo' | 'dia_libre'; NINGUNO (nada ese día
 *  aunque lo diga el patrón) o null (lo que diga el patrón). */
export async function ponerDia(userId, fecha, tipo) {
  const r = await api.put(`${RAIZ}/dia`, { user_id: userId, fecha, tipo });
  return r.data;
}

export async function ponerRango(userId, desde, hasta, tipo, soloLaborables) {
  const r = await api.put(`${RAIZ}/rango`, {
    user_id: userId, desde, hasta, tipo, solo_laborables: !!soloLaborables,
  });
  return r.data;
}

export const TIPOS = ['vacaciones', 'teletrabajo', 'dia_libre'];
export const NINGUNO = 'ninguno';

export const TEXTO_TIPO = {
  vacaciones: 'Vacaciones',
  teletrabajo: 'Teletrabajo',
  dia_libre: 'Día libre',
};

/** Tres colores bien distintos, con el texto en blanco: violeta las
 *  vacaciones, azul las de teletrabajo y gris pizarra el día libre. Ni
 *  amarillo (en la agenda es «online» y la reserva) ni rojo (bloqueos y
 *  cirugía). Los mismos que las clases aus-* de index.css. */
export const COLOR_TIPO = {
  vacaciones: '#7c3aed',
  teletrabajo: '#0369a1',
  dia_libre: '#475569',
};

/** De dónde sale un día que no se apuntó aquí: el Cuadrante (solo lectura). */
export const ORIGEN_CUADRANTE = 'cuadrante';

/** «Vacaciones», o «Vacaciones (Cuadrante)» si sale del Cuadrante. */
export function textoTipo(p) {
  const texto = TEXTO_TIPO[p?.tipo];
  if (!texto) return '';
  return p.origen === ORIGEN_CUADRANTE ? `${texto} (Cuadrante)` : texto;
}

/** La clase de index.css de ese tipo (fondo de color y letra blanca). */
export function claseTipo(tipo) {
  return TIPOS.includes(tipo) ? `aus-${tipo}` : '';
}

/** «Ana Inventada — Vacaciones · Otra — Teletrabajo · Bea — Día libre (Cuadrante)». */
export function textoPersonas(personas) {
  return (personas || [])
    .filter((p) => p && p.nombre && TEXTO_TIPO[p.tipo])
    .map((p) => `${p.nombre} — ${textoTipo(p)}`)
    .join(' · ');
}

/** «Ana Inventada» → «AI» (lo mismo que el servidor, por si no llega). */
export function iniciales(nombre) {
  const esLetra = (c) => !!c && c.toLowerCase() !== c.toUpperCase();
  const partes = String(nombre || '').replace(/-/g, ' ').split(/\s+/).filter((p) => esLetra(p[0]));
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
}

/** «2099-01-12» → «lun 12»: el día de cada línea de la leyenda de la semana. */
export function diaCorto(fecha) {
  const s = String(fecha || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }).replace('.', '');
}
